/**
 * AuditList.jsx — KPI audit queue listing
 * Fetches stepIds from getAuditAnswers before bulk-approving from the list page.
 */
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import { injectAuditStyles } from "./auditStyles";
import { useAuditData } from "./useAuditData";
import { HighlightText, AuditIcon } from "./AuditIcons";
import { fetchAuditAnswers, pendingStepIds, submitAuditAction, bulkActionKpi } from "./auditApi";
import { BulkRejectModal } from "./auditReviewParts";
import QualitativeAuditList from "./QualitativeAuditList";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { usePermission } from "../../hooks/usePermission";

/* ─── helpers ─── */
const fmt = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const injectPageStyles = () => {
  if (document.getElementById("al2-styles")) return;
  const s = document.createElement("style");
  s.id = "al2-styles";
  s.textContent = `
    .al2-search {
      height: 34px; border-radius: 7px; border: 1px solid #e5e7eb;
      background: #f9fafb; font-size: 13px; color: #111827;
      padding: 0 12px 0 34px; outline: none; font-family: inherit;
      transition: border .15s, box-shadow .15s; width: 220px;
    }
    .al2-search:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.08); background: #fff; }
    .al2-sel {
      height: 34px; border-radius: 7px; border: 1px solid #e5e7eb;
      background: #f9fafb; font-size: 13px; color: #374151;
      padding: 0 10px; cursor: pointer; outline: none; font-family: inherit; transition: border .15s;
    }
    .al2-sel:focus { border-color: #2563eb; }
    .al2-sel.on { border-color: #2563eb; background: #eff6ff; color: #1d4ed8; font-weight: 600; }
    @keyframes al2Shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    .al2-shimmer {
      background: linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%);
      background-size: 400% 100%; animation: al2Shimmer 1.3s ease infinite; border-radius: 4px;
    }
    .al2-kpi-row { transition: background .1s; border-bottom: 1px solid #f3f4f6; }
    .al2-kpi-row:hover { background: #fafbff !important; }
    .al2-kpi-row:last-child { border-bottom: none; }
  `;
  document.head.appendChild(s);
};

/* ─── Count badge ─── */
const CountBadge = ({ value, color, bg, border }) => {
  if (!value && value !== 0) return <span style={{ fontSize: 12, color: "#d1d5db" }}>—</span>;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
      color, background: bg, border: `1px solid ${border}`,
    }}>{value}</span>
  );
};

/* ─── Confirm modal ─── */
const ConfirmModal = ({ title, message, confirmLabel, onConfirm, onCancel, saving }) => (
  <div
    onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(3px)" }}
  >
    <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 400, boxShadow: "0 16px 40px rgba(0,0,0,.14)", overflow: "hidden" }}>
      <div style={{ padding: "18px 20px 14px" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{message}</div>
      </div>
      <div style={{ padding: "12px 20px 18px", display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f3f4f6" }}>
        <button className="audit-btn-secondary" onClick={onCancel} disabled={saving} style={{ fontSize: 13 }}>Cancel</button>
        <button
          className="audit-btn-approve"
          onClick={onConfirm}
          disabled={saving}
          style={{ fontSize: 13, minWidth: 120, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {saving
            ? <><span className="audit-spinner" style={{ width: 12, height: 12, borderWidth: 1.5, borderTopColor: "#16a34a", borderColor: "#bbf7d0" }} /> Approving…</>
            : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ─── Module multiselect ─── */
const ModuleSelect = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (m) => { const n = new Set(selected); n.has(m) ? n.delete(m) : n.add(m); onChange(n); };
  const label = selected.size === 0 ? "All Modules" : selected.size === 1 ? [...selected][0] : `${selected.size} modules`;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`al2-sel${selected.size ? " on" : ""}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "0 10px" }}
      >
        <span style={{ fontSize: 13 }}>{label}</span>
        <AuditIcon name="chevron" size={11} color={selected.size ? "#2563eb" : "#9ca3af"} />
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 20px rgba(0,0,0,.1)", minWidth: 190, maxHeight: 220, overflow: "auto", padding: "4px 0" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", cursor: "pointer", fontSize: 12.5, borderBottom: "1px solid #f3f4f6", fontWeight: selected.size === 0 ? 600 : 400, color: selected.size === 0 ? "#2563eb" : "#374151" }}>
            <input type="checkbox" checked={selected.size === 0} onChange={() => onChange(new Set())} style={{ accentColor: "#2563eb" }} />
            All Modules
          </label>
          {options.map(m => (
            <label key={m} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", cursor: "pointer", fontSize: 12.5, fontWeight: selected.has(m) ? 600 : 400, color: selected.has(m) ? "#2563eb" : "#374151", background: selected.has(m) ? "#eff6ff" : "transparent" }}>
              <input type="checkbox" checked={selected.has(m)} onChange={() => toggle(m)} style={{ accentColor: "#2563eb" }} />
              {m}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── KPI Row ─── */
const KpiRow = ({ item, index, searchQuery, selected, onToggle, onReview, onApproveRow, approving }) => {
  const hasPending = (item.totalPending || 0) > 0;
  const isApproving = approving === item.questionId;

  return (
    <tr className="al2-kpi-row" style={{ background: selected ? "#eff6ff" : "#fff" }}>
      {/* Checkbox */}
      <td style={{ width: 44, padding: "12px 8px 12px 16px", textAlign: "center", verticalAlign: "middle" }}>
        <input type="checkbox" className="audit-checkbox" checked={selected} onChange={() => onToggle(item.questionId)} />
      </td>

      {/* # */}
      <td style={{ width: 36, padding: "12px 4px", textAlign: "center", verticalAlign: "middle", fontSize: 11, color: "#d1d5db", fontWeight: 600 }}>
        {index + 1}
      </td>

      {/* KPI name */}
      <td style={{ padding: "12px 12px 12px 4px", verticalAlign: "middle" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
          {hasPending && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b", flexShrink: 0, marginTop: 5 }} />}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", lineHeight: 1.45, letterSpacing: "-.01em" }}>
              <HighlightText text={item.title || `KPI #${item.questionId}`} query={searchQuery} />
            </div>
            {item.frequency && (
              <span style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 2, display: "block" }}>{item.frequency}</span>
            )}
          </div>
        </div>
      </td>

      {/* Value */}
      <td style={{ width: 120, padding: "12px", textAlign: "right", verticalAlign: "middle", paddingRight: 20 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1e40af", fontVariantNumeric: "tabular-nums" }}>
          {fmt(item.cumulativeValue)}
        </span>
        {item.unit && item.unit !== "-" && (
          <span style={{ fontSize: 10.5, color: "#9ca3af", marginLeft: 4 }}>{item.unit}</span>
        )}
      </td>

      {/* Pending */}
      <td style={{ width: 90, padding: "12px 8px", textAlign: "center", verticalAlign: "middle" }}>
        <CountBadge value={item.totalPending || 0} color="#92400e" bg="#fffbeb" border="#fde68a" />
      </td>

      {/* Approved */}
      <td style={{ width: 90, padding: "12px 8px", textAlign: "center", verticalAlign: "middle" }}>
        <CountBadge value={item.totalApproved || 0} color="#166534" bg="#f0fdf4" border="#bbf7d0" />
      </td>

      {/* Rejected */}
      <td style={{ width: 90, padding: "12px 8px", textAlign: "center", verticalAlign: "middle" }}>
        <CountBadge value={item.totalRejected || 0} color="#991b1b" bg="#fef2f2" border="#fecaca" />
      </td>

      {/* Actions */}
      <td style={{ width: 240, padding: "12px 16px 12px 8px", verticalAlign: "middle" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, justifyContent: "flex-end" }}>
          {/* Approve pending — fetches stepIds first */}
          {/* {hasPending && (
            <button
              onClick={() => onApproveRow(item)}
              disabled={isApproving}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 13px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
                cursor: isApproving ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
                transition: "all .15s", opacity: isApproving ? .6 : 1,
              }}
            >
              {isApproving
                ? <><span className="audit-spinner" style={{ width: 10, height: 10, borderWidth: 1.5, borderTopColor: "#16a34a", borderColor: "#bbf7d0" }} /> Approving…</>
                : <>✓ Approve {item.totalPending}</>
              }
            </button>
          )} */}

          {/* Review */}
          <button
            onClick={() => onReview(item)}
            style={{
              padding: "5px 14px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: hasPending ? "#2563eb" : "#f3f4f6",
              color: hasPending ? "#fff" : "#9ca3af",
              border: `1px solid ${hasPending ? "#2563eb" : "#e5e7eb"}`,
              transition: "all .15s",
            }}
          >
            Review
          </button>
        </div>
      </td>
    </tr>
  );
};

/* ─── Shimmer ─── */
const ShimmerRows = () => (
  <>
    {[0, 1, 2, 3, 4].map(i => (
      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", opacity: 1 - i * .15 }}>
        <td colSpan={8} style={{ padding: "14px 16px" }}>
          <div className="al2-shimmer" style={{ height: 14, width: `${48 + (i * 13) % 38}%` }} />
        </td>
      </tr>
    ))}
  </>
);

/* ─── Bulk bar ─── */
const BulkBar = ({ count, onApprove, onReject, onDeselect, saving, approveLabel = "✓ Approve All Pending" }) => {
  if (!count) return null;
  return (
    <div className="audit-bulk-bar" style={{ background: "#1e293b", padding: "10px 24px", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
      <span style={{ color: "#94a3b8", fontSize: 13 }}>
        <span style={{ color: "#fff", fontWeight: 700 }}>{count}</span> KPI{count !== 1 ? "s" : ""} selected
      </span>
      <div style={{ flex: 1 }} />
      <button
        onClick={onApprove} disabled={saving}
        style={{ padding: "6px 16px", borderRadius: 7, border: "none", background: "#16a34a", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? .6 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        {saving
          ? <><span className="audit-spinner" style={{ width: 12, height: 12, borderWidth: 1.5, borderTopColor: "#fff", borderColor: "rgba(255,255,255,.3)" }} /> Saving…</>
          : approveLabel
        }
      </button>
      {onReject && (
        <button
          onClick={onReject} disabled={saving}
          style={{ padding: "6px 16px", borderRadius: 7, border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? .6 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          ✕ Reject
        </button>
      )}
      <button onClick={onDeselect} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #334155", background: "transparent", color: "#94a3b8", fontSize: 13, cursor: "pointer" }}>
        Deselect
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════ */
const AuditList = (props) => {
  const historyRouter = useHistory();
  const location = useLocation();
  const { currentUserId } = useCurrentUser();
  const [isAdmin] = usePermission("audit.approve-on-behalf");

  const auditType = useMemo(() => {
    const path = location.pathname || "";
    if (path.includes("internal")) return "INTERNAL";
    if (path.includes("external")) return "EXTERNAL";
    return null;
  }, [location.pathname]);

  useEffect(() => { injectAuditStyles(); injectPageStyles(); }, []);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlAuditorId = useMemo(() => queryParams.get("auditorId"), [queryParams]);
  const urlStatus = useMemo(() => queryParams.get("status"), [queryParams]);

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [search, setSearch] = useState("");
  const [fyFilter, setFyFilter] = useState("");
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [qualSelectedIds, setQualSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("quantitative");

  /**
   * The `status` query param is the single source of truth for the status filter — derived
   * from the URL rather than mirrored into local state, so it stays in sync with deep links
   * and back/forward navigation. `setStatusFilter` writes the change straight back to the URL.
   */
  const statusFilter = urlStatus || "";
  const setStatusFilter = useCallback((value) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set("status", value);
    else params.delete("status");
    historyRouter.replace({ pathname: location.pathname, search: params.toString() });
  }, [location.search, location.pathname, historyRouter]);

  /* Approve state */
  const [approvingRowId, setApprovingRowId] = useState(null);   // single row loading
  const [bulkSaving, setBulkSaving] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);   // item to confirm single approve
  const [confirmBulk, setConfirmBulk] = useState(false);  // bulk confirm

  /* Qualitative-tab bulk state */
  const [qualBulkApproveConfirm, setQualBulkApproveConfirm] = useState(false);
  const [qualBulkRejectOpen, setQualBulkRejectOpen] = useState(false);
  const [qualBulkSaving, setQualBulkSaving] = useState(false);

  const answerFilters = useMemo(() => {
    const filters = {};
    if (urlAuditorId) {
      filters.auditorId = urlAuditorId;
    }
    return filters;
  }, [urlAuditorId]);

  const { auditItems, summary, isLoading, error, financialYears, selectedFY, setSelectedFY, refetch } =
    useAuditData({ answerFilters, auditType });

  useEffect(() => {
    if (selectedFY?.id && !fyFilter) setFyFilter(String(selectedFY.id));
  }, [selectedFY?.id]); // eslint-disable-line

  const handleFyChange = (v) => {
    setFyFilter(v);
    const fy = financialYears.find(f => String(f.id) === String(v));
    if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value, financial_year_value: fy.financial_year_value });
  };

  const moduleOptions = useMemo(() => {
    const seen = new Set();
    for (const item of auditItems) { if (item.module) seen.add(item.module); }
    return [...seen].sort();
  }, [auditItems]);

  const filtered = useMemo(() => {
    let items = [...auditItems];
    if (search) items = items.filter(i => (i.title || "").toLowerCase().includes(search.toLowerCase()));
    if (statusFilter) items = items.filter(i => i.auditStatus === statusFilter);
    if (selectedModules.size > 0) items = items.filter(i => selectedModules.has(i.module));
    return items;
  }, [auditItems, search, statusFilter, selectedModules]);

  const quantitativeItems = useMemo(
    () => filtered.filter(i => (i.questionType || "") !== "tabular_question"),
    [filtered],
  );
  const qualitativeItems = useMemo(
    () => filtered.filter(i => (i.questionType || "") === "tabular_question"),
    [filtered],
  );
  // Actionable when this user is currently the assigned auditor on the KPI
  // AND there is at least one pending answer. Used to gate per-row checkboxes
  // and the Select-All header on the qualitative tab.
  const qualActionableSet = useMemo(() => {
    const s = new Set();
    for (const i of qualitativeItems) {
      const isCurrent = (i.currentAuditors || []).some(a => Number(a.id) === Number(currentUserId));
      // Admins may act on behalf of any pending answer, not just their own queue.
      if ((i.totalPending || 0) > 0 && (isAdmin || isCurrent)) s.add(i.questionId);
    }
    return s;
  }, [qualitativeItems, currentUserId, isAdmin]);
  const qualAllSelected =
    qualActionableSet.size > 0 &&
    [...qualActionableSet].every(id => qualSelectedIds.has(id));
  const qualSomeSelected = [...qualActionableSet].some(id => qualSelectedIds.has(id));
  const tabItems = activeTab === "qualitative" ? qualitativeItems : quantitativeItems;

  /* Navigate to answer review */
  const handleReview = useCallback((item) => {
    historyRouter.push({
      pathname: "/audit/answers",
      state: {
        questionId: item.questionId,
        locationIds: item.locations,
        fyId: selectedFY?.id,
        title: item.title,
        unit: item.unit,
        questionType: item.questionType,
        auditType,
      },
    });
  }, [historyRouter, selectedFY, auditType]);

  /* ── Fetch pending stepIds for a KPI (shared between single & bulk approve) ── */
  const fetchPendingStepIds = useCallback(async (item) => {
    const fyId = selectedFY?.id;
    if (!fyId) return [];
    const { rows } = await fetchAuditAnswers({
      questionId: item.questionId,
      financialYearId: fyId,
      locationIds: item.locations,
      auditorType: auditType,
    });
    return pendingStepIds(rows);
  }, [selectedFY, auditType]);

  /* Single row approve — opens confirm first */
  const handleApproveRow = useCallback((item) => {
    setConfirmRow(item);
  }, []);

  /* Confirmed single row approve */
  const doApproveRow = useCallback(async () => {
    const item = confirmRow;
    if (!item) return;
    setApprovingRowId(item.questionId);
    try {
      const stepIds = await fetchPendingStepIds(item);
      if (!stepIds.length) { alert("No pending answers found to approve."); return; }
      await submitAuditAction({
        questionId: item.questionId,
        stepIds,
        financialYearId: selectedFY?.id,
        action: "ACCEPTED",
        questionType: item.questionType,
        questionTitle: item.title,
      });
      setConfirmRow(null);
      refetch?.();
    } catch (err) {
      console.error("Approve row error:", err);
    } finally {
      setApprovingRowId(null);
    }
  }, [confirmRow, fetchPendingStepIds, selectedFY, refetch]);

  /* Bulk approve selected KPIs */
  const doBulkApprove = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkSaving(true);
    try {
      const selectedItems = quantitativeItems.filter(i => ids.includes(i.questionId));
      await Promise.all(
        selectedItems.map(item =>
          bulkActionKpi({ item, fyId: selectedFY?.id, auditType, action: "ACCEPTED", ...(isAdmin ? { onBehalf: true, finalize: true } : {}) })
        )
      );
      setSelectedIds(new Set());
      setConfirmBulk(false);
      refetch?.();
    } catch (err) {
      console.error("Bulk approve error:", err);
    } finally {
      setBulkSaving(false);
    }
  }, [selectedIds, quantitativeItems, selectedFY, auditType, refetch, isAdmin]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleQualSelect = useCallback((id) => {
    setQualSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const toggleQualAll = useCallback(() => {
    setQualSelectedIds(prev => {
      const allAlreadyChecked =
        qualActionableSet.size > 0 &&
        [...qualActionableSet].every(id => prev.has(id));
      if (allAlreadyChecked) {
        // Uncheck only the actionable ones (preserve other selection state).
        const n = new Set(prev);
        for (const id of qualActionableSet) n.delete(id);
        return n;
      }
      const n = new Set(prev);
      for (const id of qualActionableSet) n.add(id);
      return n;
    });
  }, [qualActionableSet]);

  /* Qualitative bulk approve */
  const doQualBulkApprove = useCallback(async () => {
    const ids = [...qualSelectedIds];
    if (!ids.length) return;
    setQualBulkSaving(true);
    try {
      const selectedItems = qualitativeItems.filter(i => ids.includes(i.questionId));
      await Promise.all(
        selectedItems.map(item =>
          bulkActionKpi({ item, fyId: selectedFY?.id, auditType, action: "ACCEPTED", locationIds: [1], ...(isAdmin ? { onBehalf: true, finalize: true } : {}) })
        )
      );
      setQualSelectedIds(new Set());
      setQualBulkApproveConfirm(false);
      refetch?.();
    } catch (err) {
      console.error("Qual bulk approve error:", err);
    } finally {
      setQualBulkSaving(false);
    }
  }, [qualSelectedIds, qualitativeItems, selectedFY, auditType, refetch, isAdmin]);

  /* Qualitative bulk reject (shared remark) */
  const doQualBulkReject = useCallback(async (remark) => {
    const ids = [...qualSelectedIds];
    if (!ids.length) return;
    setQualBulkSaving(true);
    try {
      const selectedItems = qualitativeItems.filter(i => ids.includes(i.questionId));
      await Promise.all(
        selectedItems.map(item =>
          bulkActionKpi({ item, fyId: selectedFY?.id, auditType, action: "REJECTED", remark, locationIds: [1], ...(isAdmin ? { onBehalf: true } : {}) })
        )
      );
      setQualSelectedIds(new Set());
      setQualBulkRejectOpen(false);
      refetch?.();
    } catch (err) {
      console.error("Qual bulk reject error:", err);
    } finally {
      setQualBulkSaving(false);
    }
  }, [qualSelectedIds, qualitativeItems, selectedFY, auditType, refetch, isAdmin]);
  // Bulk select operates on the quantitative tab only (qualitative has its own per-row action).
  const allFiltered = quantitativeItems.length > 0 && quantitativeItems.every(i => selectedIds.has(i.questionId));
  const someFiltered = quantitativeItems.some(i => selectedIds.has(i.questionId));
  const toggleAll = () => allFiltered ? setSelectedIds(new Set()) : setSelectedIds(new Set(quantitativeItems.map(i => i.questionId)));

  const hasFilters = !!(search || statusFilter || selectedModules.size > 0);
  const clearAll = () => { setSearch(""); setStatusFilter(""); setSelectedModules(new Set()); };

  return (
    <div style={{ display: "flex", background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Sidebar */}
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, height: "100vh", background: "#fff", borderRight: "1px solid #e5e7eb", zIndex: 100, transition: "flex .2s" }}>
        <Sidebar dataFromParent={props.location?.pathname || "/dashboard"} onSidebarToggle={setSidebarExpanded} />
      </div>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <Header />

        {/* ── Page header ── */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "18px 26px 14px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: "#111827", letterSpacing: "-.025em" }}>Audit Queue</h1>
                {auditType && (
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 5, background: auditType === "INTERNAL" ? "#eff6ff" : "#f5f3ff", color: auditType === "INTERNAL" ? "#2563eb" : "#7c3aed", border: `1px solid ${auditType === "INTERNAL" ? "#dbeafe" : "#ddd6fe"}`, textTransform: "uppercase", letterSpacing: ".05em" }}>
                    {auditType}
                  </span>
                )}
              </div>
              {selectedFY && (
                <span style={{ fontSize: 11.5, color: "#9ca3af", background: "#f3f4f6", padding: "2px 9px", borderRadius: 20, border: "1px solid #e5e7eb", display: "inline-block" }}>
                  {selectedFY.name}
                </span>
              )}
            </div>

            {/* Stats */}
            {!isLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                {[
                  { label: "Total", value: summary.total || auditItems.length, color: "#374151" },
                  { label: "Pending", value: summary.pending || 0, color: "#d97706" },
                  { label: "Approved", value: summary.approved || 0, color: "#16a34a" },
                  { label: "Rejected", value: summary.rejected || 0, color: "#dc2626" },
                ].map(({ label, value, color }, i, arr) => (
                  <div key={label} style={{ display: "flex", alignItems: "stretch" }}>
                    <div style={{ padding: "4px 18px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                      <div style={{ fontSize: 9.5, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", marginTop: 3 }}>{label}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ width: 1, background: "#f3f4f6", alignSelf: "stretch", margin: "4px 0" }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#9ca3af" }}>
                <AuditIcon name="search" size={13} />
              </span>
              <input className="al2-search" placeholder="Search KPI…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>

            <select className="al2-sel" value={fyFilter} onChange={e => handleFyChange(e.target.value)}>
              {financialYears.map(fy => <option key={fy.id} value={fy.id}>{fy.financial_year_value}</option>)}
            </select>

            <select className={`al2-sel${statusFilter ? " on" : ""}`} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <ModuleSelect options={moduleOptions} selected={selectedModules} onChange={setSelectedModules} />

            {hasFilters && (
              <button onClick={clearAll} style={{ height: 34, padding: "0 12px", borderRadius: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 500, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca" }}>
                × Clear
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#9ca3af" }}>
              {!isLoading && `${tabItems.length} KPI${tabItems.length !== 1 ? "s" : ""}`}
            </span>
          </div>

          {/* Tab bar — sits inside the page header so filters above stay common */}
          <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", marginTop: 14, marginBottom: -14 }}>
            {[
              { id: "quantitative", label: "Quantitative", count: quantitativeItems.length },
              { id: "qualitative", label: "Qualitative", count: qualitativeItems.length },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: "9px 22px", fontSize: 13, fontWeight: 600, border: "none", background: "transparent",
                  color: activeTab === t.id ? "#2563eb" : "#64748b",
                  borderBottom: `2px solid ${activeTab === t.id ? "#2563eb" : "transparent"}`,
                  marginBottom: -2, cursor: "pointer", transition: "color .15s, border-color .15s",
                  display: "inline-flex", alignItems: "center", gap: 7,
                }}
              >
                {t.label}
                <span style={{
                  fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                  background: activeTab === t.id ? "#eff6ff" : "#f3f4f6",
                  color: activeTab === t.id ? "#2563eb" : "#9ca3af",
                  fontVariantNumeric: "tabular-nums",
                }}>{t.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Bulk bar — tab-aware */}
        {activeTab === "quantitative" && (
          <BulkBar
            count={selectedIds.size}
            onApprove={() => setConfirmBulk(true)}
            onDeselect={() => setSelectedIds(new Set())}
            saving={bulkSaving}
            approveLabel={isAdmin ? "✓ Approve All On Behalf" : undefined}
          />
        )}
        {activeTab === "qualitative" && (
          <BulkBar
            count={qualSelectedIds.size}
            onApprove={() => setQualBulkApproveConfirm(true)}
            onReject={() => setQualBulkRejectOpen(true)}
            onDeselect={() => setQualSelectedIds(new Set())}
            saving={qualBulkSaving}
            approveLabel={isAdmin ? "✓ Approve On Behalf" : "✓ Approve"}
          />
        )}

        {/* Tab content */}
        <div style={{ flex: 1, overflow: "auto", padding: "18px 22px 36px" }}>
          {activeTab === "qualitative" ? (
            isLoading ? (
              <div style={{ background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb", padding: 24, fontSize: 12.5, color: "#9ca3af", textAlign: "center" }}>
                Loading…
              </div>
            ) : error ? (
              <div style={{ background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb", padding: 24, textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>Failed to load</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>{error}</div>
              </div>
            ) : (
              <QualitativeAuditList
                items={qualitativeItems}
                searchQuery={search}
                fyId={selectedFY?.id}
                auditType={auditType}
                onActionComplete={refetch}
                selectedIds={qualSelectedIds}
                onToggleSelect={toggleQualSelect}
                actionableSet={qualActionableSet}
                allSelected={qualAllSelected}
                someSelected={qualSomeSelected}
                onToggleAll={toggleQualAll}
              />
            )
          ) : (
            <div style={{ background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.04)" }}>
              <div style={{ overflowX: "auto", borderRadius: 9 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "1.5px solid #e5e7eb" }}>
                    <th style={{ width: 44, padding: "10px 8px 10px 16px", textAlign: "center" }}>
                      <input type="checkbox" className="audit-checkbox"
                        checked={allFiltered}
                        ref={el => { if (el) el.indeterminate = someFiltered && !allFiltered; }}
                        onChange={toggleAll}
                      />
                    </th>
                    <th style={{ width: 36, padding: "10px 4px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "center" }}>#</th>
                    <th style={{ padding: "10px 12px 10px 4px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "left" }}>KPI</th>
                    <th style={{ width: 120, padding: "10px 20px 10px 12px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "right" }}>Value</th>
                    <th style={{ width: 90, padding: "10px 8px", fontSize: 10, fontWeight: 600, color: "#d97706", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "center" }}>Pending</th>
                    <th style={{ width: 90, padding: "10px 8px", fontSize: 10, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "center" }}>Approved</th>
                    <th style={{ width: 90, padding: "10px 8px", fontSize: 10, fontWeight: 600, color: "#dc2626", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "center" }}>Rejected</th>
                    <th style={{ width: 240, padding: "10px 16px 10px 8px", fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && <ShimmerRows />}

                  {!isLoading && error && (
                    <tr><td colSpan={8} style={{ padding: "56px 24px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 6 }}>Failed to load</div>
                      <div style={{ fontSize: 12, color: "#9ca3af" }}>{error}</div>
                    </td></tr>
                  )}

                  {!isLoading && !error && quantitativeItems.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: "64px 24px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                        {auditItems.length === 0 ? "No KPIs in audit queue" : "No KPIs match your filters"}
                      </div>
                      {hasFilters && (
                        <button onClick={clearAll} style={{ fontSize: 12.5, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                          Clear filters
                        </button>
                      )}
                    </td></tr>
                  )}

                  {!isLoading && !error && quantitativeItems.map((item, idx) => (
                    <KpiRow
                      key={item.questionId}
                      item={item}
                      index={idx}
                      searchQuery={search}
                      selected={selectedIds.has(item.questionId)}
                      onToggle={toggleSelect}
                      onReview={handleReview}
                      onApproveRow={handleApproveRow}
                      approving={approvingRowId}
                    />
                  ))}
                </tbody>
              </table>
              </div>

              {/* Footer */}
              {!isLoading && !error && quantitativeItems.length > 0 && (
                <div style={{ padding: "9px 20px", borderTop: "1px solid #f3f4f6", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                    Showing {quantitativeItems.length} of {auditItems.filter(i => (i.questionType || "") !== "tabular_question").length} KPIs
                  </span>
                  <div style={{ display: "flex", gap: 14 }}>
                    {[
                      { label: "pending", color: "#d97706", count: quantitativeItems.reduce((a, i) => a + (i.totalPending || 0), 0) },
                      { label: "approved", color: "#16a34a", count: quantitativeItems.reduce((a, i) => a + (i.totalApproved || 0), 0) },
                      { label: "rejected", color: "#dc2626", count: quantitativeItems.reduce((a, i) => a + (i.totalRejected || 0), 0) },
                    ].filter(x => x.count > 0).map(({ label, color, count }) => (
                      <span key={label} style={{ fontSize: 11.5, color, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                        {count} {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Single row approve confirm */}
      {confirmRow && (
        <ConfirmModal
          title="Approve All Pending Answers"
          message={`Approve all ${confirmRow.totalPending} pending answer${confirmRow.totalPending !== 1 ? "s" : ""} for "${confirmRow.title}"? This will fetch all pending step IDs and approve them at your audit level.`}
          confirmLabel={`✓ Approve ${confirmRow.totalPending}`}
          saving={!!approvingRowId}
          onConfirm={doApproveRow}
          onCancel={() => setConfirmRow(null)}
        />
      )}

      {/* Bulk approve confirm */}
      {confirmBulk && (
        <ConfirmModal
          title={isAdmin ? "Approve On Behalf — Selected KPIs" : "Bulk Approve Selected KPIs"}
          message={isAdmin
            ? `Approve all pending answers across ${selectedIds.size} selected KPI${selectedIds.size !== 1 ? "s" : ""} on behalf of their auditors? This finalizes every remaining level (including external) for each answer.`
            : `Approve all pending answers across ${selectedIds.size} selected KPI${selectedIds.size !== 1 ? "s" : ""}? Step IDs will be fetched from each KPI before approving.`}
          confirmLabel={isAdmin ? "✓ Approve All On Behalf" : "✓ Approve All"}
          saving={bulkSaving}
          onConfirm={doBulkApprove}
          onCancel={() => setConfirmBulk(false)}
        />
      )}

      {/* Qualitative-tab bulk approve confirm */}
      {qualBulkApproveConfirm && (
        <ConfirmModal
          title="Approve Selected Qualitative KPIs"
          message={isAdmin
            ? `Approve ${qualSelectedIds.size} selected qualitative answer${qualSelectedIds.size !== 1 ? "s" : ""} on behalf of their auditors? This finalizes every remaining level for each answer.`
            : `Approve ${qualSelectedIds.size} selected qualitative answer${qualSelectedIds.size !== 1 ? "s" : ""} at your audit level?`}
          confirmLabel={`✓ Approve ${qualSelectedIds.size}`}
          saving={qualBulkSaving}
          onConfirm={doQualBulkApprove}
          onCancel={() => setQualBulkApproveConfirm(false)}
        />
      )}

      {/* Qualitative-tab bulk reject (shared remark) */}
      {qualBulkRejectOpen && (
        <BulkRejectModal
          count={qualSelectedIds.size}
          saving={qualBulkSaving}
          onConfirm={doQualBulkReject}
          onCancel={() => setQualBulkRejectOpen(false)}
        />
      )}
    </div>
  );
};

export default AuditList;